import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import { concatMap, first, map, Observable, of, withLatestFrom } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElementId, CanvasNamespaceId } from '@blaze-runtime/engine-domain';
import { getWorkflowNodeBase } from '@blaze-runtime/workflow-domain';
import { ActionConfiguratorFormField } from '@blaze-shared/connectors-domain';
import {
  ActionNodeType,
  DataBindingSourceInfo,
  transferQueryConditions,
  updateFormAssignmentsFromColumns,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BaseSchema,
  BlazeSchema,
  DatasetColumn,
  getColumnKey,
  getColumnQuery,
  isWorkflowSchema,
  matchColumn,
} from '@blaze-shared/schema-domain';
import { isNullish } from '@blaze-shared/shared-util';
import {
  appendNodeToWorkflowMutably,
  Connection,
  createFormCloseDialogNode,
  DataAssignment,
  DeleteWorkflowNode,
  FormActionType,
  InsertWorkflowNode,
  isCloseDialogNode,
  isNavigateNode,
  MAIN_INPUT_NAME,
  UpdateWorkflowNode,
  Workflow,
  WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { RuntimeFacade, State } from '../../+state';
import { DEFAULT_SUCCESS_MESSAGE } from '../../elements/form-container/form-container.model';
import {
  findSubmitWorkflowConnection,
  getOrCreateConnection,
  getUiRuntimeElement,
  getWorkflowRuntimeElement,
} from '../../utils/runtime';
import { WorkflowCrudService } from '../workflow-crud.service';

export interface SubmitWorkflowOptions {
  isDialog: boolean;
  layoutConnections?: Connection[];
  isServer?: boolean;
  upsert?: boolean;
}

@Injectable({ providedIn: 'root' })
export class FormSubmitWorkflowService {
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject<Store<State>>(Store);
  private readonly workflowCrudService = inject(WorkflowCrudService);

  /**
   * Creates or updates the submit workflow if button present
   *
   * **Note:** the workflow and connections are not added nor updated in the state
   *
   * @param projectId the ID of the project to which the workflow should be added
   * @param namespaceId the namespace for which the connections should be prepared
   * @param formId the id of the form
   * @param buttonId the id of the submit button
   * @param elementDataSource the data source used to create the form
   * @param pregenerationType the pregeneration type
   * @param options.isDialog if the provided form is in a dialog a CloseDialog action will be appended
   * @param options.layoutConnections existing connections in the namespace - should be provided when generating workflow for layouts not stored in state
   * @param options.isServer whether the workflow is generated server-side
   * @param options.upsert overrides the `Update` action node's `upsert` flag; omit to preserve whatever the existing node has
   * @returns an Observable which emits the created/updated workflow and connections for the form and button elements
   */
  createOrUpdateSubmitWorkflowAndConnections(
    projectId: string | undefined,
    namespaceId: CanvasNamespaceId,
    formId: CanvasElementId,
    buttonId: CanvasElementId | undefined,
    formActionType: FormActionType,
    elementDataSource: DataBindingSourceInfo,
    options: SubmitWorkflowOptions,
  ): Observable<
    | {
        workflow: Workflow;
        formConnection: Connection;
        buttonConnection: Connection;
      }
    | undefined
  > {
    const { isDialog, layoutConnections, isServer = false, upsert } = options;

    if (!projectId || !buttonId) {
      return of(undefined);
    }

    const connections = layoutConnections || this.runtimeFacade.connectionsValue;
    const formOutputConnection = findSubmitWorkflowConnection(connections, formId, buttonId);
    const existingWf = !formOutputConnection
      ? undefined
      : this.runtimeFacade.workflowsValue.find((wf) => wf.id === formOutputConnection.target.id);

    const wf$ = existingWf
      ? of(existingWf)
      : this.workflowCrudService.createWorkflow({
          inputs: {},
          inputSchemas: [],
          isLongRunning: false,
          isServer,
          nodes: [],
          pageId: namespaceId,
          projectId,
          startNodeId: null,
        });

    return wf$.pipe(
      // Generating a workflow is an asynchronous task - append latest connection list
      concatMap((workflow) =>
        this.schemaService
          .get(elementDataSource.id, elementDataSource.sourceType === 'context')
          .pipe(
            first(),
            map((formSchema) => ({ workflow, formSchema })),
          ),
      ),
      withLatestFrom(
        layoutConnections
          ? of(layoutConnections)
          : this.store.select(runtimeSelectors.selectConnections),
      ),
      concatMap(([{ formSchema, workflow }, connections]) => {
        const formConnection: Connection = getOrCreateConnection(
          connections,
          namespaceId,
          getUiRuntimeElement(formId),
          getWorkflowRuntimeElement(workflow.id),
          'change',
        );

        const buttonConnection: Connection = getOrCreateConnection(
          connections,
          namespaceId,
          getUiRuntimeElement(buttonId),
          getWorkflowRuntimeElement(workflow.id),
          'click',
        );

        if (formActionType === 'custom') {
          return of({
            workflow,
            formConnection,
            buttonConnection,
          });
        }

        const updatedWorkflow = produce(workflow, (draft) => {
          draft.preventAutoStart = true;

          // Ensure inputs
          const ins = Object.values(draft.inputs);
          const inputConnectionPairs = [
            [MAIN_INPUT_NAME, formConnection.id],
            ['form', formConnection.id],
            ['button', buttonConnection.id],
          ];
          for (const [inputName, connectionId] of inputConnectionPairs) {
            if (!ins.includes(connectionId)) {
              draft.inputs[inputName] = connectionId;
            }
          }

          // Update nodes
          const { actionNode, existingActionNodeIdx } = this.getActionNode(
            formActionType,
            elementDataSource,
            existingWf,
            upsert,
          );
          if (actionNode) {
            if (existingActionNodeIdx !== -1) {
              draft.nodes[existingActionNodeIdx] = actionNode;
            } else {
              appendNodeToWorkflowMutably(draft, actionNode);
            }
          }

          const existingCloseDialogNode = existingWf?.nodes.find(isCloseDialogNode);
          const existingNavigateNode = existingWf?.nodes.find(
            (n) => isNavigateNode(n) && n.id === 'success-navigate',
          );
          // if success-navigate node is present we don't need to append the close dialog node
          if (isDialog && !existingCloseDialogNode && !existingNavigateNode) {
            const outputExpression = formActionType === 'prompt' ? '$$form' : 'null';
            appendNodeToWorkflowMutably(
              draft,
              createFormCloseDialogNode(namespaceId, existingCloseDialogNode, outputExpression),
            );
          }
        });

        const inputSchemaMapping: { [key: string]: BaseSchema | undefined } = {
          button: undefined,
          form: formSchema,
          input1: formSchema,
        };
        const newConnections: Connection[] = [formConnection, buttonConnection];
        return this.workflowCrudService
          .updateWorkflow(updatedWorkflow, newConnections, inputSchemaMapping)
          .pipe(
            map((savedWorkflow) => ({
              workflow: savedWorkflow,
              formConnection,
              buttonConnection,
            })),
          );
      }),
    );
  }

  getActionNode(
    actionNodeType: ActionNodeType,
    elementDataSource: DataBindingSourceInfo,
    existingWf?: Workflow,
    upsert?: boolean,
  ): { actionNode: WorkflowNode | undefined; existingActionNodeIdx: number } {
    const sourceSchema = this.schemaService.getSchema<BlazeSchema>(
      elementDataSource.id,
      elementDataSource.sourceType === 'context',
    );
    if (!sourceSchema) {
      throw new Error('Could not find workflow output schema');
    }

    let idColumn = sourceSchema.columns.find((c) => c.isId)?.name || '';
    if (!idColumn) {
      idColumn = elementDataSource.columns.find((c) => c.isId)?.name || '';
    }
    if (!idColumn) {
      console.warn('Id column not found. The workflow query needs to be updated.');
    }

    const datasetId = isWorkflowSchema(sourceSchema) ? sourceSchema.tableId : sourceSchema.id;
    const successMessage = DEFAULT_SUCCESS_MESSAGE;

    // get the best match: by name & nodeType...
    const existingActionNodeIdx = existingWf?.nodes.findIndex((n) => n.id === 'action') ?? -1;
    const existingActionNode = existingWf?.nodes[existingActionNodeIdx];

    let actionNode: WorkflowNode | undefined;
    switch (actionNodeType) {
      case 'insert':
        actionNode = {
          ...getWorkflowNodeBase('Insert', 'action'),
          successMessage,
          runOnce: true,
          ...existingActionNode,
          nodeType: 'Insert',
          datasetId,
          assignments: updateFormAssignmentsFromColumns(
            existingActionNode,
            elementDataSource.columns,
          ),
        } as InsertWorkflowNode;
        break;

      case 'update':
        actionNode = {
          ...getWorkflowNodeBase('Update', 'action'),
          upsert: false,
          successMessage,
          runOnce: true,
          ...existingActionNode,
          nodeType: 'Update',
          currentRow: true,
          datasetId,
          assignments: updateFormAssignmentsFromColumns(
            existingActionNode,
            elementDataSource.columns,
          ),
          query: transferQueryConditions(existingActionNode, sourceSchema),
          ...(upsert !== undefined ? { upsert } : {}),
        } as UpdateWorkflowNode;
        break;

      case 'delete':
        actionNode = {
          ...getWorkflowNodeBase('Delete', 'action'),
          ...existingActionNode,
          nodeType: 'Delete',
          askForConfirmation: true,
          currentRow: true,
          datasetId,
          query: transferQueryConditions(existingActionNode, sourceSchema),
          queryConditions: [],
          runOnce: true,
        } as DeleteWorkflowNode;
        break;

      default:
        actionNode = undefined;
    }
    return { actionNode, existingActionNodeIdx };
  }
}

export function getNodeAssignmentValueSchema(column: DatasetColumn): BaseSchema {
  const valueSchema: BaseSchema = {
    id: getColumnKey(column),
    name: '',
    columns: [column],
  };
  if (column.multipleValues && column.dataTypeDef?.columns.length) {
    valueSchema.columns = [...column.dataTypeDef.columns];
  }
  return valueSchema;
}

export function getNodeActionConfiguratorFormField({
  assignmentsValue,
  column,
  isInvalid,
  nodeAssignments,
  nodeDialogAssignments,
  showOnFormMap,
}: {
  assignmentsValue?: ActionConfiguratorFormField[];
  column: DatasetColumn;
  isInvalid?: boolean;
  nodeAssignments: DataAssignment[];
  nodeDialogAssignments?: DataAssignment[];
  showOnFormMap: Record<string, boolean>;
}): { field: ActionConfiguratorFormField; wasDefined: boolean } {
  // Assignment's name can be an id
  const existingAssignmentIndex = nodeAssignments?.findIndex(({ name }) =>
    matchColumn(column, getColumnQuery(name)),
  );
  const existingAssignment = nodeAssignments?.[existingAssignmentIndex];
  const existingDialogAssignment = nodeDialogAssignments?.find(({ name }) =>
    matchColumn(column, getColumnQuery(name)),
  );
  const editAssignment = existingDialogAssignment ?? existingAssignment;
  // Look for id and fallback to name if id is not present
  const showOnForm =
    assignmentsValue?.find(({ name }) => matchColumn(column, getColumnQuery(name)))?.showOnForm ||
    (column.id && showOnFormMap[column.id] === true) ||
    (column.name && showOnFormMap[column.name] === true) ||
    false;
  const field = {
    description: column.description,
    displayInfos: editAssignment?.displayInfos || [],
    displayInfosObject: editAssignment?.displayInfosObject || {},
    displayName: column.displayName,
    hasDefaultValue: !isNullish(column.defaultValue),
    id: column.id,
    index: existingAssignment ? existingAssignmentIndex : undefined,
    isArrayValue: column.multipleValues || Array.isArray(editAssignment?.value),
    isFormulaValue: editAssignment?.isFormulaValue,
    isInvalid: !!isInvalid,
    isObjectValue: !!column.dataTypeDef?.columns.length,
    isOptional: editAssignment?.isOptional ?? true, // for backwards compatibility defaults to true when not set
    isRequired: !!column.isRequired,
    name: column.name,
    showOnForm,
    value: editAssignment?.value ?? '',
    valueSchema: getNodeAssignmentValueSchema(column),
  };
  return { field, wasDefined: !!editAssignment };
}
